import { useEffect, useState } from 'react';
import { buildApiUrl } from '../config/env';
import { fetchElementsConfigOnce, readSharedJson } from './elementsConfigCache';

/**
 * What the supporter avatar row needs from a donor list (CON-518): the first few supporters
 * as displayed, the total, and the list type the admin picked — one source for the modal
 * title and the list (Handoff map: "Supporters heading + list read one listType").
 *
 * Reads page 1 of the same `elements-config` url `SupportersList` reads first, through the
 * same in-flight dedupe, so a list that mounts at the same time costs no second request.
 * Deliberately small: no country table, no formatting — the row is in the hosted entry,
 * the list (with `i18n-iso-countries`) stays in its lazy chunk until the modal opens.
 */

const RECENT = 'RECENT_DONORS';
const TOP = 'TOP_DONORS';

/** The API answers 'recent' / 'top' as well as the enum. */
export function normaliseListType(listType) {
  if (listType === 'top' || listType === TOP) return TOP;
  return RECENT;
}

// The same four names SupportersList shows in the dashboard preview.
const PREVIEW_SUPPORTERS = ['John Smith', 'Jane Doe', 'Michael Johnson', 'Sarah Williams'].map(
  (name) => ({ name, public: true }),
);

export function toDisplayedSupporters(donors, showAllAnonymous) {
  return (Array.isArray(donors) ? donors : []).map((donor) => {
    if (showAllAnonymous) return { name: 'Someone', public: false };
    if (donor?.anonymous || !donor?.name) return { name: 'Anonymous', public: false };
    return { name: donor.name, public: true };
  });
}

function pickDonors(data, listType) {
  if (listType === TOP && data?.topDonors?.length) return data.topDonors;
  if (listType === RECENT && data?.recentDonors?.length) return data.recentDonors;
  return data?.donations || data?.recentDonors || data?.topDonors || [];
}

/**
 * @returns {{ ready: boolean, supporters: Array<{name: string, public: boolean}>, total: number,
 *   listType: 'RECENT_DONORS'|'TOP_DONORS' }}
 */
export default function useSupportersSummary({
  donorListID,
  enabled = true,
  previewMode = false,
  donorListType = RECENT,
  donorListData = null,
  showAllSupportersAnonymous = false,
}) {
  const [summary, setSummary] = useState({
    ready: false,
    supporters: [],
    total: 0,
    listType: normaliseListType(donorListType),
  });

  useEffect(() => {
    if (!enabled) return undefined;

    if (previewMode) {
      const listType = normaliseListType(donorListType);
      const donors = donorListData ? pickDonors(donorListData, listType) : null;
      const supporters = donors
        ? toDisplayedSupporters(donors, showAllSupportersAnonymous)
        : PREVIEW_SUPPORTERS;
      setSummary({ ready: true, supporters, total: supporters.length, listType });
      return undefined;
    }

    if (!donorListID) return undefined;
    let cancelled = false;
    const url = buildApiUrl('/elements-config', { elementID: donorListID, page: 1 });
    if (!url) return undefined;

    fetchElementsConfigOnce(url)
      .then((response) => {
        if (!response.ok) throw new Error(`elements-config ${response.status}`);
        return readSharedJson(response);
      })
      .then((data) => {
        if (cancelled || data?.elementType !== 'DONOR_LIST' || data.active === false) return;
        const listType = normaliseListType(data.listType);
        const donors = pickDonors(data, listType);
        const showAll = data.showAllSupportersAnonymous ?? showAllSupportersAnonymous;
        const total = Number.isFinite(data.totalDonors) ? data.totalDonors : donors.length;
        setSummary({
          ready: true,
          supporters: toDisplayedSupporters(donors, showAll),
          total,
          listType,
        });
      })
      .catch(() => {
        // The row is an entry point, not content: if the list cannot load, it stays hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, previewMode, donorListID, donorListType, donorListData, showAllSupportersAnonymous]);

  return summary;
}
